import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import qs from 'qs';
import { createInfiniteQuery , createMutation } from 'react-query-kit';

import { HTTP_HEADERS } from '@/constants';
import { useToast } from '@/hooks/useToast';
import { queryClient } from '@/services/query-client';
import { filterEmptyParams, restUrl, updatePages } from '@/services/utils';

import { JobDataType, PublicationDataType, PublicationEventDataType } from './executed.publication.types';

interface PublicationParams {
    recipient?: string;
    jobScheduleId?: number;
    offset?: number;
    limit?: number;
}
interface PublicationResponse {
    data: PublicationDataType[];
    cursor?: string;
}
const usePublicationData = createInfiniteQuery<
    PublicationResponse,
    PublicationParams,
    Error,
    string | undefined
>({
    queryKey: ['items/publications'],
    fetcher: async (variables, { pageParam }) => {
        const response = await axios.get(restUrl('publications'), {
            params: variables,
            headers: {
                [HTTP_HEADERS.X_CURSOR]: pageParam,
            },
            paramsSerializer: (params) => {
                return qs.stringify(filterEmptyParams(params));
            },
        });
        return {
            data: response.data ?? [],
            cursor: response.headers[HTTP_HEADERS.X_CURSOR] ?? undefined,
        };
    },
    getNextPageParam: (lastPageRequestResponse) =>
        lastPageRequestResponse.cursor,
    initialPageParam: undefined,
});

const invalidatePublicationJobs = (client: ReturnType<typeof useQueryClient>) => {
    client.invalidateQueries({ queryKey: ['publication/jobs'] });
};

const useRunJob = () => {
    const { toast } = useToast();
    return useMutation({
        mutationFn: (jobId: number) => {
            return axios.post(
                restUrl(`jobs/${jobId}/run`),
                null,
                { params: { calledFromFrontend: true } },
            );
        },
        onSuccess: () => {
            toast.success('PUBLICATION_JOB.PUBLICATION_STARTED_SUCCESSFULLY');
            // Do not invalidate jobs here: GET /jobs can return BUSY after
            // publicationJobChanged already delivered IDLE and overwrite it.
        },
        onError: () => {
            toast.error('PUBLICATION_JOB.PUBLICATION_STARTED_FAILED');
        },
    });
};

const useToggleJobExecutionStatus = () => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (jobId: number) => {
            return axios.post(restUrl(`jobs/${jobId}/toggleExecutionStatus`));
        },
        onSuccess: () => {
            toast.success('JOB_SCHEDULE.EXECUTION_STATUS_TOGGLED');
            invalidatePublicationJobs(queryClient);
        },
        onError: () => {
            toast.error('JOB_SCHEDULE.EXECUTION_STATUS_NOT_TOGGLED');
            invalidatePublicationJobs(queryClient);
        },
    });
};

const useDeleteJob = () => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (jobId: number) => {
            return axios.delete(restUrl(`jobs/${jobId}`));
        },
        onSuccess: () => {
            toast.success('PUBLICATION_JOB.JOB_DELETE_SUCCESS');
            invalidatePublicationJobs(queryClient);
        },
        onError: () => {
            toast.error('PUBLICATION_JOB.JOB_DELETE_FAILED');
            invalidatePublicationJobs(queryClient);
        },
    });
};

const fetchSelection = async (selectionId: string) => {
    const response = await axios.get(restUrl(`selections/${selectionId}`));
    return response.data;
};

interface JobParams {
    jobId?: string;
    offset?: number;
    limit?: number;
    type?: string;
}
interface JobResponse {
    data: JobDataType[];
    cursor?: string;
}

const useJobData = createInfiniteQuery<
    JobResponse,
    JobParams,
    Error,
    string | undefined
>({
    queryKey: ['publication/jobs'],
    fetcher: async (variables, { pageParam }) => {
        const response = await axios.get(restUrl('jobs'), {
            params: variables,
            headers: {
                [HTTP_HEADERS.X_CURSOR]: pageParam,
            },
            paramsSerializer: (params) => {
                return qs.stringify(filterEmptyParams(params));
            },
        });
        return {
            data: response.data ?? [],
            cursor:
                response.headers[HTTP_HEADERS.X_CURSOR] ?? undefined,
        };
    },
    getNextPageParam: (lastPageRequestResponse) =>
        lastPageRequestResponse.cursor,
    initialPageParam: undefined,
});

const useUpdatePublicationData = createMutation<
    void,
    {
        publicationEvent: PublicationEventDataType;
        cacheQueryVariables: Record<string, unknown>;
    },
    void
>({
    mutationFn: async ({
        publicationEvent,
        cacheQueryVariables,
    }: {
        publicationEvent: PublicationEventDataType;
        cacheQueryVariables: Record<string, unknown>;
    }) => {
        queryClient.setQueryData(
            ['items/publications', cacheQueryVariables],
            (publicationState: InfiniteData<PublicationResponse>) => {
                // Note: publicationState should not be mutated directly
                if (publicationState === undefined) {
                    return undefined;
                }
                return {
                    pageParams: publicationState.pageParams,
                    pages: updatePages(
                        publicationState.pages,
                        publicationEvent.publication,
                    ),
                };
            },
        );
    },
});
const useStopPublication = () =>
    useMutation({
        mutationFn: (publicationId: number) => {
            return axios.post(restUrl(`publications/abort/${publicationId}`));
        },
    });
const useReRunPublication = () => {
    const { toast } = useToast();
    const queryClient: any = useQueryClient();

    return useMutation({
        mutationFn: (publicationId: number) => {
            return axios.post(
                restUrl(`publications/republish/${publicationId}`),
                null,
                { params: { calledFromFrontend: true } }
            );
        },
        onSuccess: () => {
            toast.success(
                'COMMUNICATION_CHANNELS.REPUBLICATION_STARTED_SUCCESSFULLY',
            );

            queryClient.invalidateQueries(['items/publications']);
        },
        onError: () => {
            toast.error('COMMUNICATION_CHANNELS.ERROR_STARTING_REPUBLICATION');
        },
    });
};
export {
    fetchSelection,
    useDeleteJob,
    useJobData,
    usePublicationData,
    useReRunPublication,
    useRunJob,
    useStopPublication,
    useToggleJobExecutionStatus,
    useUpdatePublicationData,
};
